import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from './Toast';
import CustomSelect from './CustomSelect';
import ColumnToggle from './ColumnToggle';
import KeepaPullModal from './KeepaPullModal';
import {
  getProduct,
  getMappings,
  getCandidates,
  confirmCandidate,
  rejectCandidate,
  deleteMapping,
  deleteProduct,
  getMarketplaces,
  updateProduct,
  createMapping,
  updateMapping,
  startKeepaPull,
  runUnmatched,
  getJobProgress,
  fetchVariantRanks,
} from '../api';
import CombinedChart from './CombinedChart';
import { usePreferences } from '../PreferencesContext';

const MAPPINGS_COLUMNS = [
  { key: 'thumbnail', label: 'Thumbnail' },
  { key: 'marketplace', label: 'Marketplace' },
  { key: 'asin', label: 'ASIN' },
  { key: 'amazon_title', label: 'Amazon Title' },
  { key: 'buybox', label: 'Buy Box' },
  { key: 'fba_fee', label: 'FBA Fee' },
  { key: 'referral_fee', label: 'Referral Fee' },
  { key: 'profit_amount', label: 'Profit' },
  { key: 'profit_margin', label: 'Profit %' },
  { key: 'suggested', label: 'Suggested' },
  { key: 'confidence', label: 'Confidence' },
  { key: 'source', label: 'Source' },
  { key: 'verified', label: 'Verified' },
];

const CANDIDATES_COLUMNS = [
  { key: 'marketplace', label: 'Marketplace' },
  { key: 'asin', label: 'ASIN' },
  { key: 'amazon_title', label: 'Amazon Title' },
  { key: 'score_title', label: 'Title Score' },
  { key: 'score_brand', label: 'Brand Score' },
  { key: 'score_ean', label: 'EAN Score' },
  { key: 'score_attr', label: 'Attr Score' },
  { key: 'score_composite', label: 'Composite' },
  { key: 'status', label: 'Status' },
];

interface ProductDetailProps {
  productId: string;
  initialJobId?: string | null;
  onJobConsumed?: () => void;
  onBack: () => void;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ productId, initialJobId, onJobConsumed, onBack }) => {
  const { showToast } = useToast();
  const { isColumnVisible } = usePreferences();
  const mCol = (key: string) => isColumnVisible('mappings_table', key);
  const cCol = (key: string) => isColumnVisible('candidates_table', key);
  const [product, setProduct] = useState<any>(null);
  const [mappings, setMappings] = useState<any[]>([]);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [marketplaces, setMarketplaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Image lightbox state
  const [imageEnlarged, setImageEnlarged] = useState(false);
  useEffect(() => {
    if (!imageEnlarged) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setImageEnlarged(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [imageEnlarged]);

  // Edit product state
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [editLoading, setEditLoading] = useState(false);

  // Add manual mapping state
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [mappingForm, setMappingForm] = useState({ marketplace_id: '', asin: '', amazon_title: '', amazon_brand: '', amazon_url: '' });
  const [mappingLoading, setMappingLoading] = useState(false);

  // Edit mapping state
  const [editMappingId, setEditMappingId] = useState<string | null>(null);
  const [editMappingForm, setEditMappingForm] = useState<any>({});
  const [editMappingLoading, setEditMappingLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'variants' | 'mappings' | 'candidates' | 'charts'>('overview');
  const [variantMpTab, setVariantMpTab] = useState<string | null>(null);
  const [variantRankLoading, setVariantRankLoading] = useState<string | null>(null);

  // Keepa pull state
  const [keepaMarketplace, setKeepaMarketplace] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('asin-tracker-settings') || '{}');
      return saved.defaultMarketplace || 'GB';
    } catch { return 'GB'; }
  });
  const [keepaPulling, setKeepaPulling] = useState(false);
  const [keepaProgress, setKeepaProgress] = useState<any>(null);
  const [pullModalOpen, setPullModalOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, keepaMarketplace]);

  // Auto-start polling when navigated here from Add Product with a job already running
  useEffect(() => {
    if (!initialJobId) return;
    setKeepaPulling(true);
    setKeepaProgress(null);
    startJobPolling(initialJobId);
    onJobConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJobId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [prodRes, mpRes] = await Promise.all([
        getProduct(productId, { marketplace_code: keepaMarketplace }),
        getMarketplaces(),
      ]);
      setProduct(prodRes.data);
      setMarketplaces(mpRes.data);
    } catch (err) {
      console.error('Failed to load product:', err);
      setError('Failed to load product details');
      setLoading(false);
      return;
    }

    // Load mappings and candidates independently — permission failures on these
    // should not block the product detail from rendering.
    const [mapRes, candRes] = await Promise.allSettled([
      getMappings(productId),
      getCandidates(productId),
    ]);
    if (mapRes.status === 'fulfilled') setMappings(mapRes.value.data);
    else setMappings([]);
    if (candRes.status === 'fulfilled') setCandidates(candRes.value.data);
    else setCandidates([]);

    setLoading(false);
  };

  const clearMessages = () => { setMessage(''); setError(''); };

  const handleConfirm = async (candidateId: string) => {
    try {
      await confirmCandidate(candidateId);
      setMessage('Candidate confirmed and mapping created!');
      setError('');
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to confirm');
    }
  };

  const handleReject = async (candidateId: string) => {
    try {
      await rejectCandidate(candidateId);
      setMessage('Candidate rejected');
      setError('');
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleDeleteMapping = async (mappingId: string) => {
    if (!window.confirm('Remove this ASIN mapping?')) return;
    try {
      await deleteMapping(mappingId);
      setMessage('Mapping removed');
      setError('');
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete mapping');
    }
  };

  const handleDeleteProduct = async () => {
    if (!window.confirm('Delete this product and all its mappings/candidates? This cannot be undone.')) return;
    try {
      await deleteProduct(productId);
      onBack();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete product');
    }
  };

  // ── Edit Product ──────────────────────────────────────────────────────

  const openEditProduct = () => {
    setEditForm({
      title: product.title || '',
      brand: product.brand || '',
      category: product.category || '',
      ean: product.ean || '',
      upc: product.upc || '',
      mpn: product.mpn || '',
    });
    setShowEditProduct(true);
    clearMessages();
  };

  const handleSaveProduct = async () => {
    setEditLoading(true);
    try {
      const data: any = {};
      if (editForm.title !== product.title) data.title = editForm.title;
      if (editForm.brand !== (product.brand || '')) data.brand = editForm.brand || null;
      if (editForm.category !== (product.category || '')) data.category = editForm.category || null;
      if (editForm.ean !== (product.ean || '')) data.ean = editForm.ean || null;
      if (editForm.upc !== (product.upc || '')) data.upc = editForm.upc || null;
      if (editForm.mpn !== (product.mpn || '')) data.mpn = editForm.mpn || null;

      if (Object.keys(data).length === 0) {
        setShowEditProduct(false);
        return;
      }

      await updateProduct(productId, data);
      setMessage('Product updated successfully');
      setShowEditProduct(false);
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update product');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Add Manual Mapping ────────────────────────────────────────────────

  const openAddMapping = () => {
    const mappedIds = mappings.map((m: any) => m.marketplace_id);
    const firstUnmapped = marketplaces.find((mp: any) => !mappedIds.includes(mp.id));
    setMappingForm({
      marketplace_id: firstUnmapped ? String(firstUnmapped.id) : (marketplaces[0] ? String(marketplaces[0].id) : ''),
      asin: '',
      amazon_title: '',
      amazon_brand: '',
      amazon_url: '',
    });
    setShowAddMapping(true);
    clearMessages();
  };

  const handleSaveMapping = async () => {
    if (!mappingForm.marketplace_id || !mappingForm.asin.trim()) {
      setError('Marketplace and ASIN are required');
      return;
    }
    setMappingLoading(true);
    try {
      await createMapping(productId, {
        marketplace_id: parseInt(mappingForm.marketplace_id),
        asin: mappingForm.asin.trim().toUpperCase(),
        amazon_title: mappingForm.amazon_title || undefined,
        amazon_brand: mappingForm.amazon_brand || undefined,
        amazon_url: mappingForm.amazon_url || undefined,
      });
      setMessage('Manual ASIN mapping created successfully');
      setShowAddMapping(false);
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create mapping');
    } finally {
      setMappingLoading(false);
    }
  };

  // ── Edit Mapping ──────────────────────────────────────────────────────

  const openEditMapping = (m: any) => {
    setEditMappingId(m.id);
    setEditMappingForm({
      asin: m.asin,
      amazon_title: m.amazon_title || '',
      amazon_brand: m.amazon_brand || '',
      amazon_url: m.amazon_url || '',
      is_verified: m.is_verified,
    });
    clearMessages();
  };

  const handleSaveEditMapping = async () => {
    if (!editMappingId) return;
    setEditMappingLoading(true);
    try {
      await updateMapping(editMappingId, editMappingForm);
      setMessage('Mapping updated successfully');
      setEditMappingId(null);
      loadAll();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update mapping');
    } finally {
      setEditMappingLoading(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────

  const getMarketplaceCode = (id: number) => {
    const mp = marketplaces.find((m: any) => m.id === id);
    return mp ? mp.code : `${id}`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.9) return 'score-high';
    if (score >= 0.65) return 'score-medium';
    return 'score-low';
  };

  const getSourceBadgeClass = (source: string) => {
    if (source === 'auto_keepa') return 'badge badge-auto_keepa';
    if (source === 'manual') return 'badge badge-manual';
    return 'badge badge-pending';
  };

  const getAmazonUrl = (asin: string, marketplaceId: number) => {
    const mp = marketplaces.find((m: any) => m.id === marketplaceId);
    const domain = mp ? mp.domain : 'www.amazon.com';
    return `https://${domain}/gp/product/${asin}`;
  };

  const getCurrencySymbol = (marketplaceId: number) => {
    const mp = marketplaces.find((m: any) => m.id === marketplaceId);
    const domain = mp ? mp.domain : 'www.amazon.com';
    if (domain.includes('amazon.co.uk')) return '£';
    if (domain.includes('amazon.co.jp')) return '¥';
    if (domain.includes('amazon.com.au')) return 'A$';
    if (domain.includes('amazon.com.mx')) return 'MX$';
    if (domain.includes('amazon.com.br')) return 'R$';
    if (domain.includes('amazon.com.tr')) return '₺';
    if (domain.includes('amazon.com')) return '$';
    if (domain.includes('amazon.ca')) return 'CA$';
    if (domain.includes('amazon.in')) return '₹';
    if (domain.includes('amazon.sg')) return 'S$';
    if (domain.includes('amazon.ae')) return 'AED ';
    if (domain.includes('amazon.sa')) return 'SAR ';
    if (domain.includes('amazon.se')) return 'kr';
    if (domain.includes('amazon.pl')) return 'zł';
    // eurozone: .de .fr .it .es .nl .be .at .lu .ie etc.
    if (/amazon\.(de|fr|it|es|nl|be|at|lu|ie|pt|fi|gr|cz|ro|hu|bg|hr|sk|si|lt|lv|ee|cy|mt|dk|no)/.test(domain)) return '€';
    return '$';
  };

  const getCurrencySymbolByCode = (code?: string) => {
    switch ((code || '').toUpperCase()) {
      case 'GB': return '£';
      case 'DE':
      case 'FR':
      case 'IT':
      case 'ES':
      case 'NL':
      case 'BE':
      case 'AT':
      case 'IE':
      case 'PT':
      case 'FI':
      case 'GR':
      case 'CZ':
      case 'RO':
      case 'HU':
      case 'BG':
      case 'HR':
      case 'SK':
      case 'SI':
      case 'LT':
      case 'LV':
      case 'EE':
      case 'CY':
      case 'MT':
      case 'LU':
        return '€';
      case 'US': return '$';
      case 'JP': return '¥';
      case 'CA': return 'CA$';
      case 'AU': return 'A$';
      case 'MX': return 'MX$';
      case 'BR': return 'R$';
      case 'TR': return '₺';
      case 'IN': return '₹';
      case 'SG': return 'S$';
      case 'AE': return 'AED ';
      case 'SA': return 'SAR ';
      case 'SE': return 'kr';
      case 'PL': return 'zł';
      default: return '$';
    }
  };

  const [copiedAsin, setCopiedAsin] = useState<string | null>(null);

  const handleCopyAsin = (asin: string) => {
    navigator.clipboard.writeText(asin).then(() => {
      setCopiedAsin(asin);
      showToast('ASIN copied to clipboard', 'success');
      setTimeout(() => setCopiedAsin(null), 1500);
    });
  };

  // ── Keepa Pull ────────────────────────────────────────────────────────

  const pollKeepaProg = useCallback(async (jobId: string) => {
    try {
      const res = await getJobProgress(jobId);
      setKeepaProgress(res.data);
      if (res.data.status === 'completed' || res.data.status === 'failed') {
        stopPolling();
        setKeepaPulling(false);
        loadAll();
      }
    } catch {
      // silently retry
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPolling]);

  const startJobPolling = (jobId: string) => {
    stopPolling();
    pollKeepaProg(jobId);
    pollRef.current = setInterval(() => pollKeepaProg(jobId), 2000);
  };

  const handleKeepaPull = async () => {
    setKeepaPulling(true);
    setKeepaProgress(null);
    try {
      const res = await startKeepaPull(keepaMarketplace, undefined, [productId]);
      startJobPolling(res.data.job_id);
      showToast('Keepa data pull started', 'info');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : 'Failed to start Keepa pull', 'error');
      setKeepaPulling(false);
    }
  };

  const handleRunUnmatched = async () => {
    setKeepaPulling(true);
    setKeepaProgress(null);
    try {
      const res = await runUnmatched(keepaMarketplace, [productId]);
      startJobPolling(res.data.job_id);
      showToast('Title search started', 'info');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : 'Failed to start title search', 'error');
      setKeepaPulling(false);
    }
  };

  const keepaIsActive = keepaProgress && !['completed', 'failed'].includes(keepaProgress.status);
  const keepaPct = keepaProgress && keepaProgress.total > 0
    ? Math.round((keepaProgress.processed / keepaProgress.total) * 100)
    : 0;

  if (loading) {
    return <div className="loading"><div className="spinner"></div> Loading product...</div>;
  }

  if (!product) {
    return <div className="empty-state"><p>Product not found</p></div>;
  }

  const detailCurrencySymbol = getCurrencySymbolByCode(
    product.pricing_marketplace_code || keepaMarketplace
  );

  return (
    <div>
      <div className="back-link" onClick={onBack}>&larr; Back to Products</div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="page-header">
        <div className="page-header-text">
          <h2>{product.title}</h2>
          <p>
            SKU: {product.internal_sku} | Status:{' '}
            <span className={`badge badge-${product.match_status}`}>{product.match_status}</span>
          </p>
        </div>
      </div>

      {/* Keepa Pull Progress */}
      {keepaProgress && (
        <div className="card job-progress-card" style={{ marginBottom: '16px' }}>
          <div className="card-header">
            <span className="card-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: '-2px' }}>
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              Keepa Data Pull
            </span>
            <span className={`job-status-badge ${
              keepaProgress.status === 'completed' ? 'job-status-completed' :
              keepaProgress.status === 'failed' ? 'job-status-failed' :
              keepaProgress.status === 'waiting_for_tokens' ? 'job-status-waiting' :
              'job-status-processing'
            }`}>
              {keepaProgress.status === 'waiting_for_tokens' ? 'Waiting for Tokens' :
               keepaProgress.status === 'completed' ? 'Completed' :
               keepaProgress.status === 'failed' ? 'Failed' :
               keepaProgress.status === 'queued' ? 'Queued' : 'Processing'}
            </span>
          </div>

          <div className="job-progress-bar-container">
            <div className="job-progress-bar">
              <div
                className={`job-progress-fill ${keepaProgress.status === 'waiting_for_tokens' ? 'pulsing' : ''}`}
                style={{ width: `${keepaPct}%` }}
              />
            </div>
            <div className="job-progress-text">
              <span>{keepaProgress.processed} / {keepaProgress.total} ({keepaPct}%)</span>
            </div>
          </div>

          {keepaProgress.status === 'waiting_for_tokens' && (
            <div className="job-token-wait">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              <span>Keepa API token limit reached. Waiting for tokens to replenish...</span>
            </div>
          )}

          {keepaProgress.status === 'completed' && (
            <div className="job-completed-msg">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>
                Done.
                {keepaProgress.enriched > 0 && ` ${keepaProgress.enriched} enriched.`}
                {keepaProgress.matched > 0 && ` ${keepaProgress.matched} matched.`}
                {keepaProgress.no_match > 0 && ` ${keepaProgress.no_match} not found.`}
              </span>
            </div>
          )}

          {keepaProgress.errors?.length > 0 && (
            <div className="job-errors">
              <div className="job-errors-header">Errors ({keepaProgress.errors.length}):</div>
              <div className="job-errors-list">
                {keepaProgress.errors.slice(0, 3).map((err: string, i: number) => (
                  <div key={i} className="job-error-item">{err}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Product Info */}
      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: '10px' }}>
          <span className="card-title">Product Details</span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
            <button
              className="btn btn-primary btn-small"
              onClick={() => setPullModalOpen(true)}
              disabled={keepaPulling || !!keepaIsActive}
              title="Pull Keepa data for selected marketplaces"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              {keepaPulling ? 'Pulling…' : 'Pull Keepa Data'}
            </button>
            <div style={{ width: '1px', height: '20px', background: 'var(--border-primary)' }} />
            <button className="btn btn-secondary btn-small" onClick={openEditProduct}>
              Edit
            </button>
            <button className="btn btn-danger btn-small" onClick={handleDeleteProduct}>
              Delete
            </button>
          </div>
        </div>

        <div className="product-detail-top">
          {/* Product Image */}
          <div className="product-detail-image">
            {(product.image_url || mappings.some((m: any) => m.image_url)) ? (
              <>
                <div className="product-detail-img-wrapper" onClick={() => setImageEnlarged(true)} title="Click to enlarge">
                  <img
                    src={product.image_url || mappings.find((m: any) => m.image_url)?.image_url}
                    alt={product.title}
                    className="product-detail-img"
                  />
                  <div className="product-detail-img-zoom-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 3 21 3 21 9" />
                      <polyline points="9 21 3 21 3 15" />
                      <line x1="21" y1="3" x2="14" y2="10" />
                      <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                  </div>
                </div>
                {imageEnlarged && (
                  <div className="img-lightbox-overlay" onClick={() => setImageEnlarged(false)}>
                    <div className="img-lightbox-content" onClick={e => e.stopPropagation()}>
                      <button className="img-lightbox-close" onClick={() => setImageEnlarged(false)} aria-label="Close">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                      <img
                        src={product.image_url || mappings.find((m: any) => m.image_url)?.image_url}
                        alt={product.title}
                        className="img-lightbox-img"
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="product-detail-img-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span>No image</span>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="product-detail-info">
            <div className="pd-fields">
              <div className="pd-field">
                <span className="pd-field-label">Brand</span>
                <span className="pd-field-value">{product.brand || '\u2014'}</span>
              </div>
              <div className="pd-field">
                <span className="pd-field-label">Category</span>
                <span className="pd-field-value">{product.category || '\u2014'}</span>
              </div>
              <div className="pd-field">
                <span className="pd-field-label">Source Price</span>
                <span className="pd-field-value">
                  {product.price != null ? <span className="pd-price">{detailCurrencySymbol}{Number(product.price).toFixed(2)}</span> : '\u2014'}
                </span>
              </div>
              <div className="pd-field">
                <span className="pd-field-label">Profit</span>
                <span className="pd-field-value">
                  {product.profit_amount != null ? (
                    <span style={{ color: Number(product.profit_amount) >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                      {detailCurrencySymbol}{Number(product.profit_amount).toFixed(2)}
                    </span>
                  ) : '\u2014'}
                </span>
              </div>
              <div className="pd-field">
                <span className="pd-field-label">Profit %</span>
                <span className="pd-field-value">
                  {product.profit_margin_pct != null ? (
                    <span style={{ color: Number(product.profit_margin_pct) >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                      {Number(product.profit_margin_pct).toFixed(2)}%
                    </span>
                  ) : '\u2014'}
                </span>
              </div>
              <div className="pd-field">
                <span className="pd-field-label">EAN</span>
                <span className="pd-field-value">{product.ean ? <code>{product.ean}</code> : '\u2014'}</span>
              </div>
              <div className="pd-field">
                <span className="pd-field-label">UPC</span>
                <span className="pd-field-value">{product.upc ? <code>{product.upc}</code> : '\u2014'}</span>
              </div>
              <div className="pd-field">
                <span className="pd-field-label">MPN</span>
                <span className="pd-field-value">{product.mpn ? <code>{product.mpn}</code> : '\u2014'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Marketplace Grid */}
        <div>
          <div className="pd-section-label">Marketplace Coverage</div>
          <div className="marketplace-grid">
            {marketplaces.map((mp: any) => {
              const mapping = mappings.find((m: any) => m.marketplace_id === mp.id);
              return (
                <div key={mp.id} className={`marketplace-chip ${mapping ? 'mapped' : ''}`}>
                  <div className="chip-code">{mp.code}</div>
                  {mapping ? (
                    <div className="chip-asin" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <a
                        href={getAmazonUrl(mapping.asin, mp.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open on ${mp.domain}`}
                        style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}
                      >
                        {mapping.asin}
                      </a>
                      <button
                        onClick={() => handleCopyAsin(mapping.asin)}
                        title="Copy ASIN"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                          color: copiedAsin === mapping.asin ? 'var(--color-success)' : 'var(--text-muted)',
                          display: 'inline-flex', alignItems: 'center',
                        }}
                      >
                        {copiedAsin === mapping.asin ? (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="chip-asin" style={{ color: 'var(--text-muted)' }}>&mdash;</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────────────────────────── */}
        <div className="pd-tab-section">
          <div className="pd-tab-header">
            <div className="tab-bar" style={{ marginBottom: 0, flex: 1 }}>
              <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                Overview
              </button>
              <button className={`tab-btn ${activeTab === 'mappings' ? 'active' : ''}`} onClick={() => setActiveTab('mappings')}>
                Mappings ({mappings.length})
              </button>
              <button className={`tab-btn ${activeTab === 'candidates' ? 'active' : ''}`} onClick={() => setActiveTab('candidates')}>
                Candidates ({candidates.length})
              </button>
              {mappings.some((m: any) => m.variation_asins?.length || m.parent_asin) && (
                <button className={`tab-btn ${activeTab === 'variants' ? 'active' : ''}`} onClick={() => setActiveTab('variants')}>
                  Variants
                </button>
              )}
              {mappings.some((m: any) => m.price_history_csv || m.sales_rank_history_csv) && (
                <button className={`tab-btn ${activeTab === 'charts' ? 'active' : ''}`} onClick={() => setActiveTab('charts')}>
                  Charts
                </button>
              )}
            </div>
            {activeTab === 'mappings' && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <ColumnToggle tableKey="mappings_table" columns={MAPPINGS_COLUMNS} />
                <button className="btn btn-primary btn-small" style={{ marginBottom: '2px' }} onClick={openAddMapping}>
                  Add Manual Mapping
                </button>
              </div>
            )}
            {activeTab === 'candidates' && (
              <ColumnToggle tableKey="candidates_table" columns={CANDIDATES_COLUMNS} />
            )}
          </div>

          {/* ── Overview tab ── */}
          {activeTab === 'overview' && (
            <div className="pd-tab-content">
              {/* Per-marketplace metric cards */}
              {mappings.some((m: any) => m.buybox_price != null || m.suggested_price != null || m.sales_rank != null) && (
                <div className="pd-marketplace-metrics">
                  {mappings
                    .filter((m: any) => m.buybox_price != null || m.suggested_price != null || m.sales_rank != null)
                    .map((m: any) => {
                      const sym = getCurrencySymbol(m.marketplace_id);
                      return (
                        <div key={m.id} className="pd-mp-card">
                          <div className="pd-mp-card-header">
                            <span className="pd-mp-card-code">{getMarketplaceCode(m.marketplace_id)}</span>
                            <span className="pd-mp-card-asin">{m.asin}</span>
                          </div>
                          <div className="pd-mp-card-body">
                            {m.buybox_price != null && (
                              <div className="pd-mp-row">
                                <span className="pd-mp-row-label">Buy Box Price</span>
                                <span className="pd-mp-row-value price">{sym}{Number(m.buybox_price).toFixed(2)}</span>
                              </div>
                            )}
                            {m.fba_fee != null && (
                              <div className="pd-mp-row">
                                <span className="pd-mp-row-label">FBA Fee</span>
                                <span className="pd-mp-row-value">{sym}{Number(m.fba_fee).toFixed(2)}</span>
                              </div>
                            )}
                            {m.referral_fee != null && (
                              <div className="pd-mp-row">
                                <span className="pd-mp-row-label">Referral Fee</span>
                                <span className="pd-mp-row-value">{sym}{Number(m.referral_fee).toFixed(2)}</span>
                              </div>
                            )}
                            {m.profit_amount != null && (
                              <div className="pd-mp-row">
                                <span className="pd-mp-row-label">Profit</span>
                                <span
                                  className="pd-mp-row-value"
                                  style={{ color: Number(m.profit_amount) >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}
                                >
                                  {sym}{Number(m.profit_amount).toFixed(2)}
                                  {m.profit_margin_pct != null && ` (${Number(m.profit_margin_pct).toFixed(2)}%)`}
                                </span>
                              </div>
                            )}
                            {m.suggested_price != null && (
                              <div className="pd-mp-row">
                                <span className="pd-mp-row-label">Suggested Price</span>
                                <span className="pd-mp-row-value suggested">{sym}{Number(m.suggested_price).toFixed(2)}</span>
                              </div>
                            )}
                            {m.sales_rank != null && (
                              <div className="pd-mp-row">
                                <span className="pd-mp-row-label">Sales Rank</span>
                                <span className="pd-mp-row-value rank">#{Number(m.sales_rank).toLocaleString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Average Buy Box & Sales Rank tables */}
              {mappings.some((m: any) =>
                m.avg_price_7d != null || m.avg_price_30d != null ||
                m.avg_price_90d != null || m.avg_price_365d != null ||
                m.avg_rank_7d != null || m.avg_rank_30d != null ||
                m.avg_rank_90d != null || m.avg_rank_365d != null
              ) && (
                <div className="pd-avg-section">
                  <div className="pd-avg-section-title">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" />
                    </svg>
                    Historical Averages
                  </div>
                  {mappings
                    .filter((m: any) =>
                      m.avg_price_7d != null || m.avg_price_30d != null ||
                      m.avg_price_90d != null || m.avg_price_365d != null ||
                      m.avg_rank_7d != null || m.avg_rank_30d != null ||
                      m.avg_rank_90d != null || m.avg_rank_365d != null
                    )
                    .map((m: any) => {
                      const sym = getCurrencySymbol(m.marketplace_id);
                      const fmt = (v: number | null) => v != null ? `${sym}${Number(v).toFixed(2)}` : '\u2014';
                      const fmtRank = (v: number | null) => v != null ? `#${Number(v).toLocaleString()}` : '\u2014';
                      return (
                        <div key={m.id} className="pd-avg-card">
                          <div className="pd-avg-card-header">
                            <span>{getMarketplaceCode(m.marketplace_id)}</span>
                            <span style={{ fontFamily: 'monospace', color: 'var(--accent)', fontSize: '12px' }}>{m.asin}</span>
                          </div>
                          <table className="pd-avg-table">
                            <thead>
                              <tr>
                                <th></th>
                                <th>7d</th>
                                <th>30d</th>
                                <th>90d</th>
                                <th>365d</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Avg Price</td>
                                <td>{fmt(m.avg_price_7d)}</td>
                                <td>{fmt(m.avg_price_30d)}</td>
                                <td>{fmt(m.avg_price_90d)}</td>
                                <td>{fmt(m.avg_price_365d)}</td>
                              </tr>
                              <tr>
                                <td>Avg Rank</td>
                                <td className="rank-val">{fmtRank(m.avg_rank_7d)}</td>
                                <td className="rank-val">{fmtRank(m.avg_rank_30d)}</td>
                                <td className="rank-val">{fmtRank(m.avg_rank_90d)}</td>
                                <td className="rank-val">{fmtRank(m.avg_rank_365d)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                </div>
              )}

              {mappings.length === 0 && (
                <div className="empty-state" style={{ padding: '24px 0' }}>
                  <p>No Amazon data yet. Add a manual mapping or use Pull Keepa Data to find candidates.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Mappings tab ── */}
          {activeTab === 'mappings' && (
            <div className="pd-tab-content">
              {mappings.length === 0 ? (
                <div className="empty-state" style={{ padding: '24px' }}>
                  <p>No ASIN mappings yet. Click "Add Manual Mapping" or run matching to find candidates.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        {mCol('thumbnail') && <th style={{ width: '40px' }}></th>}
                        {mCol('marketplace') && <th>Marketplace</th>}
                        {mCol('asin') && <th>ASIN</th>}
                        {mCol('amazon_title') && <th>Amazon Title</th>}
                        {mCol('buybox') && <th>Buy Box</th>}
                        {mCol('fba_fee') && <th>FBA Fee</th>}
                        {mCol('referral_fee') && <th>Referral Fee</th>}
                        {mCol('profit_amount') && <th>Profit</th>}
                        {mCol('profit_margin') && <th>Profit %</th>}
                        {mCol('suggested') && <th>Suggested</th>}
                        {mCol('confidence') && <th>Confidence</th>}
                        {mCol('source') && <th>Source</th>}
                        {mCol('verified') && <th>Verified</th>}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings.map((m: any) => (
                        <tr key={m.id}>
                          {mCol('thumbnail') && (
                            <td>
                              {m.image_url ? (
                                <img src={m.image_url} alt="" className="product-thumb" />
                              ) : (
                                <div className="product-thumb-placeholder" style={{ width: 32, height: 32 }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                    <polyline points="21 15 16 10 5 21" />
                                  </svg>
                                </div>
                              )}
                            </td>
                          )}
                          {mCol('marketplace') && <td>{getMarketplaceCode(m.marketplace_id)}</td>}
                          {mCol('asin') && (
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <a
                                href={getAmazonUrl(m.asin, m.marketplace_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open on Amazon"
                                className="result-asin"
                                style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}
                              >
                                {m.asin}
                              </a>
                              <button
                                onClick={() => handleCopyAsin(m.asin)}
                                title="Copy ASIN"
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                                  color: copiedAsin === m.asin ? 'var(--color-success)' : 'var(--text-muted)',
                                  verticalAlign: 'middle', marginLeft: '4px',
                                }}
                              >
                                {copiedAsin === m.asin ? (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                ) : (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                )}
                              </button>
                            </td>
                          )}
                          {mCol('amazon_title') && (
                            <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.amazon_title || '\u2014'}
                            </td>
                          )}
                          {mCol('buybox') && (
                            <td>
                              {m.buybox_price != null ? (
                                <span className="buybox-price">{getCurrencySymbol(m.marketplace_id)}{Number(m.buybox_price).toFixed(2)}</span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                              )}
                            </td>
                          )}
                          {mCol('fba_fee') && (
                            <td>
                              {m.fba_fee != null ? (
                                <span>{getCurrencySymbol(m.marketplace_id)}{Number(m.fba_fee).toFixed(2)}</span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                              )}
                            </td>
                          )}
                          {mCol('referral_fee') && (
                            <td>
                              {m.referral_fee != null ? (
                                <span>{getCurrencySymbol(m.marketplace_id)}{Number(m.referral_fee).toFixed(2)}</span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                              )}
                            </td>
                          )}
                          {mCol('profit_amount') && (
                            <td>
                              {m.profit_amount != null ? (
                                <span style={{ color: Number(m.profit_amount) >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                                  {getCurrencySymbol(m.marketplace_id)}{Number(m.profit_amount).toFixed(2)}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                              )}
                            </td>
                          )}
                          {mCol('profit_margin') && (
                            <td>
                              {m.profit_margin_pct != null ? (
                                <span style={{ color: Number(m.profit_margin_pct) >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                                  {Number(m.profit_margin_pct).toFixed(2)}%
                                </span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                              )}
                            </td>
                          )}
                          {mCol('suggested') && (
                            <td>
                              {m.suggested_price != null ? (
                                <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
                                  {getCurrencySymbol(m.marketplace_id)}{Number(m.suggested_price).toFixed(2)}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--text-muted)' }}>&mdash;</span>
                              )}
                            </td>
                          )}
                          {mCol('confidence') && (
                            <td>
                              {m.match_confidence != null && (
                                <>
                                  <div className="score-bar">
                                    <div
                                      className={`score-fill ${getScoreColor(m.match_confidence)}`}
                                      style={{ width: `${m.match_confidence * 100}%` }}
                                    ></div>
                                  </div>
                                  {(m.match_confidence * 100).toFixed(1)}%
                                </>
                              )}
                              {m.match_confidence == null && <span style={{ color: 'var(--text-muted)' }}>Manual</span>}
                            </td>
                          )}
                          {mCol('source') && <td><span className={getSourceBadgeClass(m.source)}>{m.source}</span></td>}
                          {mCol('verified') && <td>{m.is_verified ? 'Yes' : 'No'}</td>}
                          <td>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button className="btn btn-secondary btn-small" onClick={() => openEditMapping(m)}>Edit</button>
                              <button className="btn btn-danger btn-small" onClick={() => handleDeleteMapping(m.id)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Candidates tab ── */}
          {activeTab === 'candidates' && (
            <div className="pd-tab-content">
              {candidates.length === 0 ? (
                <div className="empty-state" style={{ padding: '24px' }}>
                  <p>No match candidates found.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        {cCol('marketplace') && <th>Marketplace</th>}
                        {cCol('asin') && <th>ASIN</th>}
                        {cCol('amazon_title') && <th>Amazon Title</th>}
                        {cCol('score_title') && <th>Title</th>}
                        {cCol('score_brand') && <th>Brand</th>}
                        {cCol('score_ean') && <th>EAN</th>}
                        {cCol('score_attr') && <th>Attr</th>}
                        {cCol('score_composite') && <th>Composite</th>}
                        {cCol('status') && <th>Status</th>}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((c: any) => (
                        <tr key={c.id}>
                          {cCol('marketplace') && <td>{getMarketplaceCode(c.marketplace_id)}</td>}
                          {cCol('asin') && (
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <a
                                href={getAmazonUrl(c.asin, c.marketplace_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="result-asin"
                                style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}
                              >
                                {c.asin}
                              </a>
                              <button
                                onClick={() => handleCopyAsin(c.asin)}
                                title="Copy ASIN"
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                                  color: copiedAsin === c.asin ? 'var(--color-success)' : 'var(--text-muted)',
                                  verticalAlign: 'middle', marginLeft: '4px',
                                }}
                              >
                                {copiedAsin === c.asin ? (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                ) : (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                )}
                              </button>
                            </td>
                          )}
                          {cCol('amazon_title') && (
                            <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {c.amazon_title || '\u2014'}
                            </td>
                          )}
                          {cCol('score_title') && <td>{c.score_title != null ? `${(c.score_title * 100).toFixed(0)}%` : '\u2014'}</td>}
                          {cCol('score_brand') && <td>{c.score_brand != null ? `${(c.score_brand * 100).toFixed(0)}%` : '\u2014'}</td>}
                          {cCol('score_ean') && <td>{c.score_ean != null ? `${(c.score_ean * 100).toFixed(0)}%` : '\u2014'}</td>}
                          {cCol('score_attr') && <td>{c.score_attr != null ? `${(c.score_attr * 100).toFixed(0)}%` : '\u2014'}</td>}
                          {cCol('score_composite') && (
                            <td>
                              <div className="score-bar">
                                <div
                                  className={`score-fill ${getScoreColor(c.score_composite)}`}
                                  style={{ width: `${c.score_composite * 100}%` }}
                                ></div>
                              </div>
                              <strong>{(c.score_composite * 100).toFixed(1)}%</strong>
                            </td>
                          )}
                          {cCol('status') && <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>}
                          <td>
                            {c.status === 'pending' && (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button className="btn btn-confirm btn-small" onClick={() => handleConfirm(c.id)}>Confirm</button>
                                <button className="btn btn-reject btn-small" onClick={() => handleReject(c.id)}>Reject</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Variants tab ── */}
          {activeTab === 'variants' && (() => {
            const varMappings = mappings.filter((m: any) => m.variation_asins?.length || m.parent_asin);
            const activeVarId = variantMpTab || (varMappings[0]?.id ?? null);
            const activeVar = varMappings.find((m: any) => m.id === activeVarId) || varMappings[0];

            if (!activeVar) return null;

            const mp = marketplaces.find((x: any) => x.id === activeVar.marketplace_id);
            const domain = mp ? mp.domain : 'www.amazon.com';
            const mpCode = getMarketplaceCode(activeVar.marketplace_id);
            const allDims: string[] = [];
            (activeVar.variation_asins || []).forEach((v: any) => {
              Object.keys(v.attributes || {}).forEach((d: string) => {
                if (!allDims.includes(d)) allDims.push(d);
              });
            });
            const varCount = activeVar.variation_count ?? activeVar.variation_asins?.length ?? 0;
            const hasRankData = activeVar.variation_asins?.some((v: any) => v.sub_ranks?.length > 0);
            const rankPulledAt = activeVar.variation_asins?.find((v: any) => v.rank_pulled_at)?.rank_pulled_at;

            const handleFetchRanks = async () => {
              setVariantRankLoading(activeVar.id);
              try {
                const res = await fetchVariantRanks(activeVar.id);
                setMappings((prev: any[]) => prev.map((m: any) => m.id === activeVar.id ? res.data : m));
                showToast('Variant ranks updated', 'success');
              } catch (err: any) {
                showToast(err?.response?.data?.detail || 'Failed to fetch variant ranks', 'error');
              } finally {
                setVariantRankLoading(null);
              }
            };

            const rankColor = (rank: number) => {
              if (rank <= 1000) return 'var(--success)';
              if (rank <= 10000) return 'var(--warning)';
              return 'var(--text-secondary)';
            };

            return (
              <div className="pd-tab-content">
                {varMappings.length > 1 && (
                  <div className="pd-var-tabs">
                    {varMappings.map((m: any) => {
                      const code = getMarketplaceCode(m.marketplace_id);
                      const count = m.variation_count ?? m.variation_asins?.length ?? 0;
                      return (
                        <button
                          key={m.id}
                          className={`pd-var-tab ${m.id === activeVarId ? 'active' : ''}`}
                          onClick={() => setVariantMpTab(m.id)}
                        >
                          {code}
                          <span className="pd-var-tab-count">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="pd-var-card">
                  <div className="pd-var-card-header">
                    <div className="pd-var-card-header-left">
                      <strong>{mpCode}</strong>
                      <span className="pd-mp-card-asin">{activeVar.asin}</span>
                      {activeVar.parent_asin && (
                        <a
                          href={`https://${domain}/dp/${activeVar.parent_asin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pd-var-parent-badge"
                          style={{ textDecoration: 'none' }}
                        >
                          Parent: {activeVar.parent_asin}
                        </a>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {rankPulledAt && (
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          Ranks updated {new Date(rankPulledAt).toLocaleDateString()}
                        </span>
                      )}
                      {activeVar.variation_asins?.length > 0 && (
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={handleFetchRanks}
                          disabled={variantRankLoading === activeVar.id}
                          style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                        >
                          {variantRankLoading === activeVar.id ? (
                            <>
                              <svg className="column-toggle-spinner" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                              </svg>
                              Fetching…
                            </>
                          ) : (
                            <>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
                              </svg>
                              {hasRankData ? 'Refresh Ranks' : 'Fetch Variant Ranks'}
                            </>
                          )}
                        </button>
                      )}
                      <span className="pd-var-count">
                        {varCount} variation{varCount !== 1 ? 's' : ''} in family
                      </span>
                    </div>
                  </div>

                  {activeVar.variation_asins?.length > 0 ? (
                    <div className="pd-var-scroll">
                      <table className="pd-var-table">
                        <thead>
                          <tr>
                            <th>ASIN</th>
                            {allDims.map((d) => <th key={d}>{d}</th>)}
                            <th>
                              Sub-Category Ranks
                              {!hasRankData && (
                                <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6, fontSize: '11px' }}>
                                  — click Fetch to load
                                </span>
                              )}
                            </th>
                            <th>Amazon</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeVar.variation_asins
                            .slice()
                            .sort((a: any, b: any) => {
                              const aTop = a.sub_ranks?.[0]?.rank ?? Infinity;
                              const bTop = b.sub_ranks?.[0]?.rank ?? Infinity;
                              return aTop - bTop;
                            })
                            .map((v: any) => (
                            <tr key={v.asin} className={v.asin === activeVar.asin ? 'pd-var-current-row' : ''}>
                              <td>
                                <span className={`pd-var-asin ${v.asin === activeVar.asin ? 'current' : ''}`}>
                                  {v.asin}
                                </span>
                                {v.asin === activeVar.asin && (
                                  <span className="pd-var-star">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                                  </span>
                                )}
                              </td>
                              {allDims.map((d) => (
                                <td key={d} style={{ color: v.attributes?.[d] ? undefined : 'var(--text-muted)' }}>
                                  {v.attributes?.[d] || '—'}
                                </td>
                              ))}
                              <td>
                                {v.sub_ranks?.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    {v.sub_ranks.map((sr: any) => (
                                      <div key={sr.category_id} style={{ display: 'flex', alignItems: 'baseline', gap: '6px', lineHeight: 1.3 }}>
                                        <span style={{
                                          fontWeight: 700,
                                          fontVariantNumeric: 'tabular-nums',
                                          fontSize: '12px',
                                          color: rankColor(sr.rank),
                                          minWidth: '52px',
                                        }}>
                                          #{sr.rank.toLocaleString()}
                                        </span>
                                        {sr.category_name && (
                                          <span style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.3 }}>
                                            {sr.category_name}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>
                                )}
                              </td>
                              <td>
                                <a
                                  href={`https://${domain}/dp/${v.asin}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="pd-var-link"
                                >
                                  View ↗
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state" style={{ padding: '24px' }}>
                      <p>No variation data available for this marketplace.</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── Charts tab ── */}
          {activeTab === 'charts' && (
            <div className="pd-tab-content">
              {mappings.filter((m: any) => m.price_history_csv || m.sales_rank_history_csv).map((m: any) => (
                <div key={m.id} style={{ marginBottom: '12px' }}>
                  {mappings.filter((x: any) => x.price_history_csv || x.sales_rank_history_csv).length > 1 && (
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 500 }}>
                      {getMarketplaceCode(m.marketplace_id)} &mdash; {m.asin}
                    </div>
                  )}
                  <CombinedChart
                    priceHistoryCsv={m.price_history_csv || ''}
                    salesRankCsv={m.sales_rank_history_csv || ''}
                    currencySymbol={getCurrencySymbol(m.marketplace_id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Edit Product Modal ────────────────────────────────────────────── */}
      {showEditProduct && (
        <div className="modal-overlay" onClick={() => setShowEditProduct(false)}>
          <div className="card" style={{ maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
            <div className="card-header">
              <span className="card-title">Edit Product</span>
              <button className="modal-close" onClick={() => setShowEditProduct(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div className="form-group">
              <label>Title *</label>
              <input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Brand</label>
                <input
                  value={editForm.brand}
                  onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <input
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>EAN</label>
                <input
                  value={editForm.ean}
                  onChange={(e) => setEditForm({ ...editForm, ean: e.target.value })}
                  maxLength={13}
                />
              </div>
              <div className="form-group">
                <label>UPC</label>
                <input
                  value={editForm.upc}
                  onChange={(e) => setEditForm({ ...editForm, upc: e.target.value })}
                  maxLength={12}
                />
              </div>
            </div>

            <div className="form-group">
              <label>MPN</label>
              <input
                value={editForm.mpn}
                onChange={(e) => setEditForm({ ...editForm, mpn: e.target.value })}
              />
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditProduct(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveProduct} disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Manual Mapping Modal ──────────────────────────────────────── */}
      {showAddMapping && (
        <div className="modal-overlay" onClick={() => setShowAddMapping(false)}>
          <div className="card" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
            <div className="card-header">
              <span className="card-title">Add Manual ASIN Mapping</span>
              <button className="modal-close" onClick={() => setShowAddMapping(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Marketplace *</label>
                <select
                  value={mappingForm.marketplace_id}
                  onChange={(e) => setMappingForm({ ...mappingForm, marketplace_id: e.target.value })}
                >
                  {marketplaces.map((mp: any) => {
                    const alreadyMapped = mappings.some((m: any) => m.marketplace_id === mp.id);
                    return (
                      <option key={mp.id} value={mp.id} disabled={alreadyMapped}>
                        {mp.code} - {mp.domain}{alreadyMapped ? ' (mapped)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="form-group">
                <label>ASIN *</label>
                <input
                  value={mappingForm.asin}
                  onChange={(e) => setMappingForm({ ...mappingForm, asin: e.target.value })}
                  placeholder="e.g. B0DSJGP8Q8"
                  maxLength={10}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Amazon Title</label>
              <input
                value={mappingForm.amazon_title}
                onChange={(e) => setMappingForm({ ...mappingForm, amazon_title: e.target.value })}
                placeholder="Optional - will be fetched from Keepa if blank"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Amazon Brand</label>
                <input
                  value={mappingForm.amazon_brand}
                  onChange={(e) => setMappingForm({ ...mappingForm, amazon_brand: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group">
                <label>Amazon URL</label>
                <input
                  value={mappingForm.amazon_url}
                  onChange={(e) => setMappingForm({ ...mappingForm, amazon_url: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddMapping(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveMapping} disabled={mappingLoading}>
                {mappingLoading ? 'Creating...' : 'Create Mapping'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Mapping Modal ────────────────────────────────────────────── */}
      {editMappingId && (
        <div className="modal-overlay" onClick={() => setEditMappingId(null)}>
          <div className="card" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
            <div className="card-header">
              <span className="card-title">Edit Mapping</span>
              <button className="modal-close" onClick={() => setEditMappingId(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div className="form-group">
              <label>ASIN</label>
              <input
                value={editMappingForm.asin}
                onChange={(e) => setEditMappingForm({ ...editMappingForm, asin: e.target.value })}
                maxLength={10}
              />
            </div>

            <div className="form-group">
              <label>Amazon Title</label>
              <input
                value={editMappingForm.amazon_title}
                onChange={(e) => setEditMappingForm({ ...editMappingForm, amazon_title: e.target.value })}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Amazon Brand</label>
                <input
                  value={editMappingForm.amazon_brand}
                  onChange={(e) => setEditMappingForm({ ...editMappingForm, amazon_brand: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Amazon URL</label>
                <input
                  value={editMappingForm.amazon_url}
                  onChange={(e) => setEditMappingForm({ ...editMappingForm, amazon_url: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editMappingForm.is_verified}
                  onChange={(e) => setEditMappingForm({ ...editMappingForm, is_verified: e.target.checked })}
                  style={{ width: 'auto', marginRight: '8px' }}
                />
                Verified
              </label>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditMappingId(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveEditMapping} disabled={editMappingLoading}>
                {editMappingLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pullModalOpen && (
        <KeepaPullModal
          productIds={[productId]}
          preselectedCodes={mappings.map((m: any) => m.marketplace_code).filter(Boolean)}
          contextLabel={product?.title}
          onClose={() => setPullModalOpen(false)}
          onStarted={(jobIds) => {
            setPullModalOpen(false);
            if (jobIds.length > 0) {
              setKeepaPulling(true);
              setKeepaProgress(null);
              startJobPolling(jobIds[jobIds.length - 1]);
              showToast(`Keepa pull started for ${jobIds.length} marketplace(s)`, 'info');
            }
          }}
        />
      )}
    </div>
  );
};

export default ProductDetail;
